import { FormEvent, useState } from "react";
import { api } from "../lib/api";
import type { AssistantAnswer } from "@shared/types";

const EXAMPLES = [
  "What is the current model lineage for the Ross River floodplain?",
  "Why did roughness values change between model generations?",
  "Which models depend on older terrain?",
  "Which knowledge gaps remain unresolved?",
  "Which planning decisions depend on this model?",
];

export function Assistant({ system = "ross-river" }: { system?: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<AssistantAnswer | null>(null);

  async function ask(question: string) {
    setBusy(true);
    try {
      const res = await api<AssistantAnswer>("/api/ask", { method: "POST", body: JSON.stringify({ question, system }) });
      setAnswer(res);
    } catch (err) {
      setAnswer({
        answer: String(err),
        citations: [],
        factVsInference: "Error",
        insufficientEvidence: true,
        conflicts: [],
        knowledgeGaps: [],
      });
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (q.trim()) void ask(q.trim());
  }

  return (
    <div className="assistant">
      <button className="btn assistant-toggle" type="button" onClick={() => setOpen((o) => !o)}>
        Ask Floodplains
      </button>
      {open && (
        <div className="assistant-box">
          <p className="kicker">Ask Floodplains</p>
          <p className="muted small">Answers cite OneNetwork evidence objects. The assistant will not invent parameters.</p>
          <form onSubmit={onSubmit}>
            <textarea value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about lineage, assumptions, gaps, decisions…" />
            <button className="btn" type="submit" disabled={busy}>
              {busy ? "Reading the graph…" : "Ask"}
            </button>
          </form>
          <div className="suggest-chips" style={{ marginTop: 10 }}>
            {EXAMPLES.map((ex) => (
              <button key={ex} type="button" className="btn btn-ghost" style={{ padding: "6px 8px", fontSize: 10 }} onClick={() => { setQ(ex); void ask(ex); }}>
                {ex}
              </button>
            ))}
          </div>
          {answer && (
            <div style={{ marginTop: 14 }}>
              <p style={{ whiteSpace: "pre-wrap" }}>{answer.answer}</p>
              <p className="cite">Fact vs inference: {answer.factVsInference}</p>
              {answer.insufficientEvidence && <p className="muted small">Insufficient evidence — the source is silent or the object is unverified.</p>}
              {answer.citations.map((c) => (
                <p key={c.evidenceId} className="cite">
                  [{c.evidenceId}] {c.title}
                </p>
              ))}
              {answer.knowledgeGaps.map((g) => (
                <p key={g} className="muted small">
                  Gap: {g}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
